/**
 * Reiter „Chat“ im Seitenpanel: die Prozess-Konversation, die offene Frage darin und — getrennt —
 * die Rückfragen an die Unterlagen.
 *
 * Die Konversation zeigt die echte Chat-Komponente (`ChatExchange`), hier **nur zum Lesen**: geantwortet
 * wird in der markierten offenen Frage darunter, mit denselben Knöpfen wie im Block „Offene Aufgabe“
 * oben — eine Antwort, ein Weg. Für Antwortarten, die das Panel nicht selbst kann (Auswahl,
 * Formular, Checkliste), bleibt die Chat-Komponente bedienbar.
 *
 * Rückfragen gehen an `…/ask` und schreiben nichts in die Konversation: in einer
 * Prozess-Konversation ist Schreiben gleich Antworten, also darf eine Frage zu den Unterlagen dort
 * nicht landen (tri-server docs/conversation-ask.md).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { Loader2, MessageSquare, Search } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { ChatExchange } from "../../components/ChatExchange";
import { userAPI } from "../../UserAPI";
import { authenticationAtom } from "../../State";
import { relativeTime } from "./tenderPanelText";

export function TenderPanelChat({
  conversationId,
  conversation,
  error,
  answerableHere,
  question,
  answers,
  highlight,
  run,
  onConversationChange,
  onAnswered,
}: {
  conversationId: string;
  /** Loaded by the panel, so the open-task block and this tab show the same state. */
  conversation: any | null;
  error: string | null;
  /** The panel can answer this task itself (Ja/Nein, Bestätigen, Freitext). */
  answerableHere: boolean;
  /** The open question, or null when nothing waits for the viewer. */
  question: string | null;
  /** The answer controls — the same element the open-task block renders. */
  answers: React.ReactNode;
  /** Bumped by „Im Chat ansehen“: scroll to the question and mark it, every time. */
  highlight: number;
  run: { running: boolean; since?: string } | null;
  onConversationChange: (conversation: any) => void;
  onAnswered: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [asked, setAsked] = useState<{ question: string; answer: string; at?: string }[]>([]);
  const [marked, setMarked] = useState(highlight > 0);

  useEffect(() => {
    let cancelled = false;
    userAPI
      .getConversationAskThread(authentication.customer, conversationId)
      .then((thread: any) => {
        if (!cancelled) setAsked(thread?.entries ?? []);
      })
      .catch(() => {
        /* Best-effort: the tab works without the earlier questions. */
      });
    return () => {
      cancelled = true;
    };
  }, [authentication.customer, conversationId]);

  useEffect(() => {
    if (!highlight) return;
    setMarked(true);
    // After the tab has painted — the question sits below the conversation.
    const scroll = window.setTimeout(
      () => document.getElementById("tender-panel-open-question")?.scrollIntoView({ behavior: "smooth", block: "center" }),
      80
    );
    const fade = window.setTimeout(() => setMarked(false), 4000);
    return () => {
      window.clearTimeout(scroll);
      window.clearTimeout(fade);
    };
  }, [highlight]);

  const submitAsk = async () => {
    const value = ask.trim();
    if (!value || asking) return;
    setAsking(true);
    setAskError(null);
    try {
      const result = await userAPI.askConversation(authentication.customer, conversationId, value);
      setAsked((result as any)?.entries ?? []);
      setAsk("");
    } catch (err: any) {
      setAskError(err?.message ?? String(err));
    } finally {
      setAsking(false);
    }
  };

  // Read-only display: without `activeTask` the chat renders no task controls of its own. Where the
  // panel cannot answer (select, form, checklist), the task stays on it and the chat answers.
  const displayed = conversation && answerableHere ? { ...conversation, activeTask: undefined } : conversation;

  return (
    <div className="space-y-4" data-testid="tender-panel-chat">
      {run?.running && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          <span className="font-medium">{t("tenderBoard.panel.chat.running")}</span>
          {run.since && (
            <span className="text-xs text-muted-foreground">
              {t("tenderBoard.panel.chat.since", { when: relativeTime(run.since, new Date()) })}
            </span>
          )}
        </div>
      )}

      {conversation ? (
        <ChatExchange
          conversation={displayed}
          showSuggestions={false}
          showLogo={false}
          height="50vh"
          readOnly={answerableHere || !conversation?.activeTask}
          readOnlyHint={
            answerableHere
              ? t("tenderBoard.panel.chat.answerBelow")
              : t("tenderBoard.panel.chat.nothingToAnswer")
          }
          hideMessageActions
          updateConversation={onConversationChange}
          onStreamComplete={onAnswered}
          onCompleteTaskClosed={async () => onAnswered()}
        />
      ) : error ? (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      ) : (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 size={16} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
        </div>
      )}

      {question && answerableHere && (
        <div
          id="tender-panel-open-question"
          data-testid="tender-panel-open-question"
          className={`rounded-md border-2 bg-amber-50 p-3 transition-shadow ${
            marked ? "border-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.25)]" : "border-amber-300"
          }`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            {t("tenderBoard.panel.chat.openQuestion")}
          </div>
          <div className="mt-1 text-sm font-semibold text-foreground">{question}</div>
          <div className="mt-3">{answers}</div>
        </div>
      )}

      <section className="rounded-md border border-dashed border-border p-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Search size={14} aria-hidden /> {t("tenderBoard.ask.title")}
          <span className="font-normal text-muted-foreground">– {t("tenderBoard.panel.chat.askNote")}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("tenderBoard.ask.hint")}</p>

        <div className="mt-3 flex items-center gap-2">
          <input
            className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-ring"
            placeholder={t("tenderBoard.ask.placeholder")}
            aria-label={t("tenderBoard.ask.title")}
            value={ask}
            disabled={asking}
            onChange={(event) => setAsk(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submitAsk();
            }}
          />
          <Button variant="outline" disabled={asking || !ask.trim()} onClick={submitAsk} className="gap-1.5">
            {asking && <Loader2 size={14} className="animate-spin" aria-hidden />}
            {t("tenderBoard.panel.chat.askSubmit")}
          </Button>
        </div>
        {askError && <div className="mt-2 text-xs text-red-700">{askError}</div>}

        {asked.length > 0 && (
          <div className="mt-3 space-y-3">
            {[...asked].reverse().map((entry, index) => (
              <div key={index} className="rounded border border-border bg-background p-2">
                <div className="text-sm font-medium">{entry.question}</div>
                <div className="tender-md mt-1 text-sm">
                  <Markdown remarkPlugins={[remarkGfm]}>{entry.answer}</Markdown>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="text-[11px] text-muted-foreground underline hover:text-foreground"
              onClick={async () => {
                await userAPI.clearConversationAskThread(authentication.customer, conversationId);
                setAsked([]);
              }}
            >
              {t("tenderBoard.ask.clear")}
            </button>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(`/chatbot?conversationId=${conversationId}`)}>
          <MessageSquare size={14} aria-hidden /> {t("tenderBoard.conversation.openInChat")}
        </Button>
      </div>
    </div>
  );
}
